import { fmtBRL, fmtInt } from "@/lib/api";
import { PencilSimple, Plus, Palette } from "@phosphor-icons/react";
import { itemColorStatus } from "@/components/orders/orderDetailUtils";

export default function OrderItemsCard({
  order, silkOptions, canAdmin, totalCups,
  onEditItems, onEditColors, onUpdateSilk, onUpdateArt, onItemImageFile,
}) {
  return (
    <div className="border border-black bg-white" data-testid="order-items-skus">
      <div className="border-b border-black p-4 flex items-center justify-between gap-4 flex-wrap">
        <div className="overline text-zinc-500">ITENS DO PEDIDO ({order.order_items?.length || 0})</div>
        <button onClick={onEditItems} className="btn btn-ghost text-xs" data-testid="edit-items-btn">
          <PencilSimple size={14} weight="bold" /> Editar Itens
        </button>
      </div>
      {(!order.order_items || order.order_items.length === 0) ? (
        <div className="p-10 text-center">
          <div className="text-zinc-400 mb-3">Nenhum item</div>
          <button onClick={onEditItems} className="btn"><Plus size={14} weight="bold" /> Adicionar itens</button>
        </div>
      ) : (
        <div className="divide-y divide-zinc-200">
          {order.order_items.map((it, i) => {
            const status = itemColorStatus(it);
            return (
              <div key={it.sku || `it-${i}`} className="p-5" data-testid={`order-item-${i}`}>
                <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1 flex-wrap">
                      <span className="font-mono font-bold text-lg">{it.sku}</span>
                      <span className="text-xs px-2 py-0.5 bg-zinc-100 border border-zinc-300 uppercase tracking-widest">{it.type}</span>
                    </div>
                    <div className="text-sm text-zinc-700">{it.name}</div>
                    <div className="text-xs text-zinc-500 mt-1 font-mono">
                      {it.qty}× × {it.units_per_unit} = <strong className="text-black text-base">{fmtInt(it.cups)} copos</strong> · {fmtBRL(it.total_price)}
                    </div>
                  </div>
                  <button
                    onClick={() => onEditColors(it, i)}
                    className="btn btn-ghost text-xs"
                    data-testid={`edit-colors-${i}`}
                  >
                    <Palette size={14} weight="bold" /> {status.complete ? "Cores OK" : "Definir Cores"}
                  </button>
                </div>
                {/* Silk color inline per item */}
                <div className="mb-3 flex items-center gap-2 flex-wrap">
                  <span className="overline text-zinc-500 text-[10px]">COR DO SILK</span>
                  <select
                    className={`input text-sm py-1 px-2 w-48 ${it.silk_color ? "bg-black text-white font-bold" : "bg-yellow-50"}`}
                    value={it.silk_color || ""}
                    onChange={(e) => onUpdateSilk(i, e.target.value)}
                    data-testid={`item-silk-select-${i}`}
                  >
                    <option value="">— Selecionar cor —</option>
                    {silkOptions.map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
                  </select>
                  {it.silk_color && (
                    <span className="text-[10px] text-[#00A300] font-bold uppercase tracking-widest" data-testid={`item-silk-${i}`}>✓ SILK: {it.silk_color}</span>
                  )}
                </div>

                {/* Arte + observações por item */}
                <div className="mb-3 grid grid-cols-1 md:grid-cols-2 gap-3 border-t border-zinc-100 pt-3">
                  <div>
                    <label className="overline text-zinc-500 text-[10px] block mb-1">IMAGEM DA ARTE (deste item)</label>
                    {it.image_url ? (
                      <div className="flex items-start gap-2">
                        <img
                          src={it.image_url}
                          alt={`arte item ${i + 1}`}
                          className="border border-black max-h-24 object-contain bg-zinc-50"
                          data-testid={`item-image-${i}`}
                        />
                        <div className="flex flex-col gap-1">
                          <label className="text-[10px] underline cursor-pointer">
                            Substituir
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => onItemImageFile(i, e)}
                              className="hidden"
                              data-testid={`item-image-replace-${i}`}
                            />
                          </label>
                          <button
                            type="button"
                            onClick={() => onUpdateArt(i, "image_url", null)}
                            className="text-[10px] underline text-red-600 text-left"
                            data-testid={`item-image-remove-${i}`}
                          >
                            Remover
                          </button>
                        </div>
                      </div>
                    ) : (
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => onItemImageFile(i, e)}
                        className="input text-xs py-1"
                        data-testid={`item-image-upload-${i}`}
                      />
                    )}
                  </div>
                  <div>
                    <label className="overline text-zinc-500 text-[10px] block mb-1">OBSERVAÇÕES (deste item)</label>
                    <textarea
                      className="input text-xs min-h-[64px] py-1"
                      placeholder="Instruções específicas para este item..."
                      defaultValue={it.production_notes || ""}
                      onBlur={(e) => e.target.value !== (it.production_notes || "") && onUpdateArt(i, "production_notes", e.target.value)}
                      data-testid={`item-notes-${i}`}
                    />
                  </div>
                </div>
                {/* Color breakdown row */}
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] font-bold uppercase tracking-widest" style={{ color: status.color }}>{status.label}</span>
                  {(it.color_breakdown || []).length > 0 && (
                    <div className="flex gap-1 flex-wrap">
                      {(it.color_breakdown || []).map((cb) => (
                        <span key={`${it.sku}-${cb.color}`} className="text-xs px-2 py-1 bg-black text-white font-mono" data-testid={`item-color-${i}-${cb.color}`}>
                          {cb.color} <span className="text-[#FFC000] font-bold">{cb.qty}</span>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          <div className="p-4 bg-black text-white flex items-center justify-between">
            <span className="overline">TOTAL DO PEDIDO</span>
            <div className="flex gap-6 items-baseline">
              {canAdmin && (
                <span className="font-mono text-lg">{fmtBRL(order.total_value)}</span>
              )}
              <span className="font-mono text-2xl font-bold text-[#FFC000]">{fmtInt(totalCups)} copos</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
